// @flow
import FilterSidebarLayer from 'esm-web-pro/containers/tables/FilterSidebarLayer';
import * as React from 'react';
import { FormattedMessage, injectIntl, type IntlShape } from 'react-intl';
import { Route } from 'react-router-dom';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { addBreadcrumbs, removeBreadcrumb } from 'esm-web-pro/redux/breadcrumb';
import { closeAllPanels, closePanel } from 'esm-web-pro/redux/side-panel';
import {
    Box, ThemeContext,
} from 'grommet';
import NotificationTypePage from 'esm-web-pro/containers/notifications/NotificationTypePage';
import { Add as AddIcon } from 'grommet-icons';
import Table from 'speed-js-react-web/src/containers/grommet2/tables/Table';
import ModelPage from 'esm-web-pro/containers/pages/ModelPage';
import permissionRequired from 'esm-web-pro/decorators/permissionRequired';
import Spinner from 'esm-web-pro/components/forms/Spinner';
import styled from 'styled-components';
import EditModelPage from 'esm-web-pro/containers/pages/EditModelPage';
import 'esm-web-pro/styles/react-table.less';
import FullPagination from 'esm-web-pro/containers/tables/pagination/FullPagination';
import ColumnSettingsFormComponent from 'esm-web-pro/containers/tables/ColumnSettingsFormComponent';
import connectAPI from 'speed-js-react/src/api/connectAPI';
import ApiClient from 'speed-js-core/src/api/ApiClient';
import ViewerContext from 'speed-js-react-web/src/components/grommet2/tables/columns/Action/viewer/context';
import { get } from 'lodash';
import Refresh from 'esm-web-pro/components/tables/Refresh';
import ContextNavbar from 'esm-web-pro/components/layout/ContextNavbar';
import { CONTEXT_NAVBAR_HEIGHT } from 'esm-web-pro/styles/theme';
import NewObjectButton from 'esm-web-pro/containers/pages/NewObjectButton';

type Props = {
    resourceName: string,
    title: string,
    columns: Array<Object>,
    newObjectUrl: string,
    newObjectLabel: string,
    editObjectLabel: string,
    editObjectUrl: string,
    editObjectNestedInitials?: boolean,
    editObjectUseFormInitial?: boolean,
    editObjectInitialsCallback?: (initials) => Object,
    infoObjectLabel: string,
    infoObjectUrl: string,
    createRoute: string,
    expandRow: (row: Object) => React.Node,
    history: { push: (location: string) => void, replace: (replace: string) => void },
    match: Object,
    headerButtons: Array<React.Node>,
    headerChildren?: React.ElementType,
    resourceMethod: string,
    resourceArguments: any,
    redirectTo: string,
    addBreadcrumbs: (title: string, link: string) => void,
    removeBreadcrumb: (title: string) => void,
    intl: IntlShape,
    tableId: string,
    getOpened: boolean,
    getOpenedItems: (resourceName: string) => Promise<Object>,
    getSchema?: (
        resource: string, resourceMethod: string, resourceArguments: any, additionalQuery?: Object,
    ) => Promise<Object>,
    openedItems: Object,
    additionalQuery?: Object,
    schemaAdditionalQuery?: Object,
    filterExcludeFields?: Array<string>,
    filterUnsetPaths?: Array<string>,
    addCreateRoute?: boolean,
    newObjectFormProps?: Object,
    editObjectFormProps?: Object,
    onDataRetrieval: Function,
    onValuesChanged?: Function,
    onClose?: Function,
    excludeColumns?: Array<number>,
    createButtonPermissions?: Array<string>,
    refreshCount?: number,
    onReloadTableData?: Function,
    enableReinitialize: Boolean,
    blockingSpinner?: boolean,
    spinnerType: string,
    showSearchField?: boolean,
    saveAndNewOption?: boolean,
    customForm: React.ElementType,
    showCheckboxButton: boolean,
    checkboxesKey: string,
    checkboxCustomFormComponent: React.FC,
    getTableRowProps?: Function,
    exportBtnOnClick?: Function,
    showExportPanel?: boolean,
};

export const ListPageContext: React.Context<Object> = React.createContext(null);

const Add = styled(AddIcon)`
    border: solid 1px;
    border-color: ${(props) => props.theme.global.colors.brand};
    border-radius: 4px;
    padding: 6px;
    width: 18px;
    height: 18px;
`;

// eslint-disable-next-line max-len
const MapDispatchToProps = (dispatch) => (bindActionCreators({ addBreadcrumbs, removeBreadcrumb, closePanel }, dispatch));

@connect(null, MapDispatchToProps, null, { forwardRef: true })
@permissionRequired()
class ListPage extends React.PureComponent<Props> {
    static defaultProps: Object;

    table: { current: Table | null };

    state: { loading: boolean };

    addingNew: boolean = false;

    constructor(props: Props) {
        super(props);
        this.table = React.createRef();
        this.state = {
            loading: true,
        };
    }

    componentDidMount() {
        const {
            addBreadcrumbs: addBreadcrumbsAction,
            title,
            match,
            getOpened,
            getSchema,
            resourceName,
            resourceMethod,
            resourceArguments,
            schemaAdditionalQuery,
        } = this.props;

        if (title) {
            addBreadcrumbsAction([
                {
                    title,
                    link: match.url,
                },
            ]);
        }

        if (getSchema) {
            getSchema(resourceName, resourceMethod, resourceArguments, schemaAdditionalQuery);
        }

        if (getOpened) {
            const { getOpenedItems } = this.props;
            getOpenedItems(resourceName);
        }
    }

    componentDidUpdate(prevProps: CombinedProps): void {
        const {
            title, addBreadcrumbs: addBreadcrumbsAction,
            removeBreadcrumb: removeBreadcrumbAction, match,
        } = this.props;
        if (title && prevProps.title !== title) {
            removeBreadcrumbAction(prevProps.title);
            addBreadcrumbsAction([
                {
                    title,
                    link: match.url,
                },
            ]);
        }
    }

    componentWillUnmount() {
        const {
            removeBreadcrumb: removeBreadcrumbAction, title,
        } = this.props;
        if (title) {
            removeBreadcrumbAction(title);
        }
    }

    getCreateButton = () => {
        const {
            createButtonPermissions,
        } = this.props;

        const NewObjectComponent = permissionRequired(createButtonPermissions)(NewObjectButton);
        return (
            <NewObjectComponent {...this.props} />
        );
    };

    setLoading = (loading) => this.setState({ loading });

    handleClose = () => {
        const { onClose, onReloadTableData } = this.props;
        if (this.table.current) {
            this.table.current.reloadData(onReloadTableData);
        }
        if (onClose) {
            onClose();
        }
    };

    render() {
        const {
            resourceName, title, columns, expandRow, newObjectLabel, editObjectLabel,
            editObjectUrl, editObjectNestedInitials, createRoute, match, headerButtons,
            resourceMethod, resourceArguments, redirectTo, editObjectUseFormInitial,
            editObjectInitialsCallback, tableId, openedItems, infoObjectUrl, additionalQuery,
            filterExcludeFields, addCreateRoute, headerChildren, newObjectFormProps, editObjectFormProps,
            onDataRetrieval, onValuesChanged, excludeColumns, filterUnsetPaths, refreshCount, onReloadTableData,
            enableReinitialize, blockingSpinner, spinnerType, showSearchField, saveAndNewOption, getTableRowProps,
            exportBtnOnClick, showExportPanel, customForm, showCheckboxButton, checkboxCustomFormComponent,
            checkboxesKey,
        } = this.props;

        const { loading } = this.state;

        const formattedUpdate = (<FormattedMessage id="listpage-update" defaultMessage="Update" />);

        return (
            <ViewerContext.Provider value={openedItems}>
                <ListPageContext.Provider value={this}>
                    <Box alignContent="center">
                        <ThemeContext.Consumer>
                            {(theme: Object) => (
                                <ThemeContext.Extend value={theme?.table}>
                                    <Box margin={{ top: CONTEXT_NAVBAR_HEIGHT }}>
                                        <Table
                                            resourceName={resourceName}
                                            columns={columns}
                                            ref={this.table}
                                            expandRow={expandRow}
                                            loadingSpinner={
                                                (
                                                    <Spinner
                                                        loading={loading}
                                                        blocking={blockingSpinner}
                                                        type={spinnerType}
                                                    />
                                                )
                                            }
                                            setLoading={this.setLoading}
                                            resourceMethod={resourceMethod}
                                            resourceArguments={resourceArguments}
                                            excludeColumns={excludeColumns}
                                            filter={FilterSidebarLayer}
                                            BottomPagination={FullPagination}
                                            highlight
                                            columnSettingsFormComponent={ColumnSettingsFormComponent}
                                            headerComponent={ContextNavbar}
                                            headerChildren={headerChildren}
                                            onDataRetrieval={onDataRetrieval}
                                            additionalActions={(
                                                <>
                                                    {headerButtons.map((headerButton) => (
                                                        <Box
                                                            key={headerButton.key}
                                                            alignSelf="center"
                                                            margin={{ horizontal: 'xsmall' }}
                                                        >
                                                            {headerButton}
                                                        </Box>
                                                    ))}
                                                    {newObjectLabel && (
                                                        this.getCreateButton()
                                                    )}
                                                </>
                                            )}
                                            exportBtnOnClick={exportBtnOnClick}
                                            showExportPanel={showExportPanel}
                                            tableId={tableId || `${match.url
                                                .replace(/(^\/)(.+?)(\/*$)/, (m, g1, g2) => g2)
                                                .replace(/\//g, '-')}-table`}
                                            additionalQuery={additionalQuery}
                                            filterExcludeFields={filterExcludeFields}
                                            filterUnsetPaths={filterUnsetPaths}
                                            refreshIcon={<Refresh />}
                                            refreshCount={refreshCount}
                                            onReloadTableData={onReloadTableData}
                                            showSearchField={showSearchField}
                                            isLoading={loading}
                                            showCheckboxButton={showCheckboxButton}
                                            checkboxCustomFormComponent={checkboxCustomFormComponent}
                                            checkboxesKey={checkboxesKey}
                                            getTableRowProps={getTableRowProps}
                                        />
                                    </Box>
                                </ThemeContext.Extend>
                            )}
                        </ThemeContext.Consumer>
                        {addCreateRoute && (
                            <Route
                                path={createRoute || `${match.url}/new`.replace(/\/\//g, '/')}
                                render={(props) => (
                                    <ModelPage
                                        {...props}
                                        resourceName={resourceName}
                                        resourceArguments={resourceArguments}
                                        redirectTo={redirectTo || `${match.url}`}
                                        title={(
                                            <>
                                                {newObjectLabel}
                                            </>
                                        )}
                                        customForm={customForm}
                                        onClose={this.handleClose}
                                        onSave={this.handleClose}
                                        handleSubmitAndNew={this.handleSubmitAndNew}
                                        newObjectFormProps={newObjectFormProps}
                                        onValuesChanged={onValuesChanged}
                                        enableReinitialize={enableReinitialize}
                                        saveAndNewOption={saveAndNewOption}
                                    />
                                )}
                                exact
                            />
                        )}
                        <Route
                            path={editObjectUrl || `${match.url}/update/:id`}
                            render={(props) => (
                                <EditModelPage
                                    {...props}
                                    resourceName={resourceName}
                                    resourceArguments={resourceArguments}
                                    title={editObjectLabel || (
                                        <>
                                            {formattedUpdate}
                                            {' '}
                                            {title}
                                        </>
                                    )}
                                    redirectTo={redirectTo || `${match.url}`}
                                    onClose={this.handleClose}
                                    nestedInitials={editObjectNestedInitials}
                                    useFormInitial={editObjectUseFormInitial}
                                    initialsCallback={editObjectInitialsCallback}
                                    editObjectFormProps={editObjectFormProps}
                                />
                            )}
                            exact
                        />
                        <Route
                            path={infoObjectUrl || `${match.url}/info/:id`}
                            render={(props) => (
                                <NotificationTypePage
                                    {...props}
                                    resourceName={resourceName}
                                    resourceArguments={resourceArguments}
                                    redirectTo={match.url}
                                    onClose={this.handleClose}
                                />
                            )}
                            exact
                        />
                    </Box>
                </ListPageContext.Provider>
            </ViewerContext.Provider>
        );
    }
}

ListPage.defaultProps = {
    newObjectUrl: null,
    newObjectLabel: null,
    infoObjectUrl: null,
    createRoute: null,
    editObjectUrl: null,
    editObjectNestedInitials: false,
    editObjectUseFormInitial: false,
    editObjectInitialsCallback: undefined,
    headerButtons: [],
    headerChildren: undefined,
    redirectTo: null,
    tableId: '',
    getOpened: false,
    additionalQuery: undefined,
    schemaAdditionalQuery: undefined,
    filterExcludeFields: [],
    filterUnsetPaths: [],
    getSchema: undefined,
    addCreateRoute: true,
    newObjectFormProps: undefined,
    editObjectFormProps: undefined,
    onDataRetrieval: undefined,
    onValuesChanged: undefined,
    onClose: undefined,
    excludeColumns: [],
    createButtonPermissions: [],
    refreshCount: 0,
    onReloadTableData: undefined,
    blockingSpinner: true,
    spinnerType: 'climbingBox',
    showSearchField: false,
    saveAndNewOption: false,
    showCheckboxButton: false,
    checkboxesKey: undefined,
    checkboxCustomFormComponent: undefined,
    getTableRowProps: undefined,
    exportBtnOnClick: undefined,
    showExportPanel: undefined,
};

const apiCallback = (apiClient: ApiClient) => ({
    getOpenedItems: {
        send: (resource) => apiClient.resources[resource].opened(),
        success: (response) => ({ openedItems: response }),
    },
    getSchema: {
        send: (resource, resourceMethod, resourceArguments = [], additionalQuery) => {
            let finalResourceArguments = resourceArguments;
            if (additionalQuery && (!resourceArguments || !resourceArguments.length)) {
                finalResourceArguments = [additionalQuery];
            }
            if (resourceMethod && resourceMethod !== 'list') {
                return get(apiClient.resources, resource)
                    .schemaMethod(...finalResourceArguments, resourceMethod);
            }

            return get(apiClient.resources, resource).schema(...finalResourceArguments);
        },
        success: (schema: Object) => ({ schema }),
    },
});

export default connectAPI(apiCallback, true)(injectIntl(ListPage, { forwardRef: true }));
